/**
 * batch-65 finqle-verzamelbon (FO-484) — samengevoegde werkbonnen als ÉÉN
 * verzamelbon naar Finqle: één document, één facturatieverzoek, één
 * klantgoedkeuring, één factuur.
 *
 * Gedeeld contract tussen API, backoffice en klantportaal: de statussen en
 * labels, de DTO's (backoffice-detail, lijst-/planbord-referentie, klantportaal
 * zonder bedragen), de Zod-schema's die form en API allebei gebruiken
 * (klantbeoordeling van een bundel, bundelperiode per klant) en de foutcode van
 * de losse-verzending-waarschuwing. De pure rekenregels (nummer, totalen,
 * bundelvoorstel) staan in `@streep/utils` (`verzamelbon.ts`).
 */
import { z } from 'zod'

// ─── Bundelperiode per klant (spec §D) ───────────────────────────────────────

export const FINQLE_BUNDEL_PERIODES = ['PER_BON', 'WEEK', 'MAAND'] as const
export type FinqleBundelPeriode = (typeof FINQLE_BUNDEL_PERIODES)[number]

export const FINQLE_BUNDEL_PERIODE_LABELS: Record<FinqleBundelPeriode, string> = {
  PER_BON: 'Per werkbon',
  WEEK: 'Per week',
  MAAND: 'Per maand',
}

export const FINQLE_BUNDEL_PERIODE_UITLEG: Record<FinqleBundelPeriode, string> = {
  PER_BON: 'Geen vaste bundeling. Werkbonnen gaan los of in een bundel die je zelf samenstelt.',
  WEEK:
    'De werkbonnenlijst stelt per week één verzamelbon voor (alle goedgekeurde, nog niet verzonden bonnen van deze klant per BV). Een losse verzending vraagt eerst om bevestiging.',
  MAAND:
    'De werkbonnenlijst stelt per maand één verzamelbon voor (alle goedgekeurde, nog niet verzonden bonnen van deze klant per BV). Een losse verzending vraagt eerst om bevestiging.',
}

/** Maximale lengte van de (optionele) reden bij het wijzigen van de bundelperiode. */
export const FINQLE_BUNDEL_PERIODE_REDEN_MAX = 500

/** PUT /clients/:id/finqle-bundelperiode (CRM.canEdit). */
export const FinqleBundelPeriodeRequestSchema = z.object({
  finqleBundelPeriode: z.enum(FINQLE_BUNDEL_PERIODES, {
    error: 'Kies per werkbon, per week of per maand.',
  }),
  reden: z
    .string()
    .trim()
    .max(FINQLE_BUNDEL_PERIODE_REDEN_MAX, `Maximaal ${FINQLE_BUNDEL_PERIODE_REDEN_MAX} tekens.`)
    .nullable()
    .optional(),
})
export type FinqleBundelPeriodeRequest = z.infer<typeof FinqleBundelPeriodeRequestSchema>

export interface FinqleBundelPeriodeWijzigingDTO {
  op: string
  doorNaam: string | null
  van: FinqleBundelPeriode | null
  naar: FinqleBundelPeriode
  reden: string | null
}

export interface FinqleBundelPeriodeDTO {
  clientId: string
  finqleBundelPeriode: FinqleBundelPeriode
  /** De laatste geauditeerde wijziging; null = nooit via de app gewijzigd. */
  laatsteWijziging: FinqleBundelPeriodeWijzigingDTO | null
}

// ─── Documentvariant (FO-507, batch-67) ──────────────────────────────────────

/**
 * Welk document een verzamelbon is. `UREN` = de verzameling werkbonnen met uren
 * per dag (geen tarief, bedrag of btw; Finqle bepaalt de prijs) - altijd op de
 * Finqle-route. `MET_BEDRAGEN` = de regeltabel met bedragen en een totaal; alleen
 * voor eigen facturatie (bijlage bij een eigen factuur) en de verzamelbonnen van
 * voor batch-67.
 */
export const VERZAMELBON_VARIANTEN = ['UREN', 'MET_BEDRAGEN'] as const
export type VerzamelbonVariant = (typeof VERZAMELBON_VARIANTEN)[number]

export const VERZAMELBON_VARIANT_LABELS: Record<VerzamelbonVariant, string> = {
  UREN: 'Alleen uren (geen bedragen)',
  MET_BEDRAGEN: 'Met tarieven en bedragen',
}

export const VERZAMELBON_VARIANT_UITLEG: Record<VerzamelbonVariant, string> = {
  UREN: 'Per werkbon de uren per dag (begin, eind, pauze, netto), reis als aantallen en een urentotaal. Geen tarief, bedrag of btw.',
  MET_BEDRAGEN: 'De regeltabel met aantal, tarief en bedrag en een totaal met btw, zoals op een eigen factuur.',
}

/** Waar de verzamelbon heen gaat: naar Finqle (mail of API) of als bijlage bij een eigen factuur. */
export type VerzamelbonRoute = 'FINQLE' | 'EIGEN_FACTUUR'

/**
 * Foutcode (422) van `POST /verzamelbonnen/specificatie` wanneer voor een klant
 * die via Finqle factureert expliciet om de bedragvariant wordt gevraagd: naar
 * Finqle gaat nooit een document met bedragen (ook niet met de hand).
 */
export const VERZAMELBON_VARIANT_NIET_TOEGESTAAN = 'VERZAMELBON_VARIANT_NIET_TOEGESTAAN' as const

/** Maximale lengte van de (optionele) reden bij het wijzigen van de klantinstelling. */
export const VERZAMELBON_VARIANT_REDEN_MAX = 500

/**
 * PUT /clients/:id/verzamelbon-variant (CRM.canEdit). `variant: null` = terug naar
 * de standaard (Finqle altijd UREN; eigen facturatie MET_BEDRAGEN).
 */
export const VerzamelbonKlantVariantRequestSchema = z.object({
  variant: z
    .enum(VERZAMELBON_VARIANTEN, { error: 'Kies alleen uren of met bedragen.' })
    .nullable(),
  reden: z
    .string()
    .trim()
    .max(VERZAMELBON_VARIANT_REDEN_MAX, `Maximaal ${VERZAMELBON_VARIANT_REDEN_MAX} tekens.`)
    .nullable()
    .optional(),
})
export type VerzamelbonKlantVariantRequest = z.infer<typeof VerzamelbonKlantVariantRequestSchema>

export interface VerzamelbonKlantVariantWijzigingDTO {
  op: string
  doorNaam: string | null
  van: VerzamelbonVariant | null
  naar: VerzamelbonVariant | null
  reden: string | null
}

export interface VerzamelbonKlantVariantDTO {
  clientId: string
  /** De expliciete klantkeuze; null = de standaard. */
  variant: VerzamelbonVariant | null
  /** Wat er per route daadwerkelijk gebruikt wordt (de Finqle-route is altijd UREN). */
  effectief: { finqle: 'UREN'; eigenFacturatie: VerzamelbonVariant }
  laatsteWijziging: VerzamelbonKlantVariantWijzigingDTO | null
}

/**
 * POST /verzamelbonnen/specificatie (eigen facturatie, FO-484 par. 6.5). Zonder
 * `variant` geldt de klantinstelling (default MET_BEDRAGEN).
 */
export const VerzamelbonSpecificatieRequestSchema = z.object({
  werkbonIds: z.array(z.string().min(1)).min(1, 'Selecteer minstens één werkbon.').max(50, 'Maximaal 50 werkbonnen.'),
  variant: z.enum(VERZAMELBON_VARIANTEN).optional(),
})
export type VerzamelbonSpecificatieRequest = z.infer<typeof VerzamelbonSpecificatieRequestSchema>

/**
 * GET /verzamelbonnen/rapport/met-bedragen - de verzamelbonnen die MET bedragen
 * naar Finqle gingen (van voor batch-67), met zending-id, zodat de PO weet welke
 * dat waren (spec 207 par. 2.5).
 */
export interface VerzamelbonMetBedragenRapportItemDTO {
  id: string
  nummer: string
  zendingId: string
  status: VerzamelbonStatus
  kanaal: VerzamelbonKanaal
  clientName: string
  entityName: string
  verzondenOp: string | null
  aantalWerkbonnen: number
  bedragInclBtw: string | null
  /** Het facturatieverzoek (doorklik; alleen met FACTUREN.canView) en de Finqle-id als bekend. */
  billingRequestId: string | null
  finqleId: string | null
}

// ─── Verzamelbon: statussen, kanalen, uitval ─────────────────────────────────

export const VERZAMELBON_STATUSSEN = ['VERZONDEN', 'TE_VERSTUREN', 'VERVANGEN', 'AFGEKEURD'] as const
export type VerzamelbonStatus = (typeof VERZAMELBON_STATUSSEN)[number]

export const VERZAMELBON_STATUS_LABELS: Record<VerzamelbonStatus, string> = {
  VERZONDEN: 'Verzonden naar Finqle',
  TE_VERSTUREN: 'Klaar om te versturen',
  VERVANGEN: 'Vervangen',
  AFGEKEURD: 'Afgekeurd door de klant',
}

export const VERZAMELBON_KANALEN = ['MAIL', 'FINQLE_API'] as const
export type VerzamelbonKanaal = (typeof VERZAMELBON_KANALEN)[number]

export const VERZAMELBON_KANAAL_LABELS: Record<VerzamelbonKanaal, string> = {
  MAIL: 'Per mail naar het Finqle-adres',
  FINQLE_API: 'Via de Finqle-koppeling (API)',
}

export const VERZAMELBON_UITVAL_REDENEN = ['KLANT_AFGEKEURD', 'OPNIEUW_VERZONDEN', 'GECREDITEERD'] as const
export type VerzamelbonUitvalReden = (typeof VERZAMELBON_UITVAL_REDENEN)[number]

export const VERZAMELBON_UITVAL_LABELS: Record<VerzamelbonUitvalReden, string> = {
  KLANT_AFGEKEURD: 'Afgekeurd door de klant',
  OPNIEUW_VERZONDEN: 'Opnieuw verzonden in een andere verzamelbon',
  GECREDITEERD: 'Gecrediteerd',
}

// ─── Referentie op werkbonnenlijst, detail en planbord (spec §C.7) ───────────

/**
 * "In bundel VB-…" op een werkbon: de laatste verzamelbon waar hij (nog) in zit.
 * `verzoekId` alleen met FACTUREN.canView (doorklik naar het facturatieverzoek).
 */
export interface WerkbonBundelRefDTO {
  verzamelbonId: string
  nummer: string
  status: VerzamelbonStatus
  /** Aantal bonnen dat nu in de bundel zit (zonder uitvallers). */
  aantalWerkbonnen: number
  verzoekId: string | null
}

// ─── Backoffice: detail + lijst ──────────────────────────────────────────────

export interface VerzamelbonRegelDTO {
  werkbonId: string
  werkbonNumber: string
  positie: number
  categorie: string
  omschrijving: string
  /** Decimal-strings (nooit float over de lijn). */
  aantal: string
  eenheid: string
  /** batch-67 (FO-507): null bij de urenvariant. */
  tarief: string | null
  bedrag: string | null
  /** batch-67 (FO-507) - de urenvariant: werkdag, tijden, factuurpauze, km, medewerker. */
  datum: string | null
  beginTijd: string | null
  eindTijd: string | null
  pauzeMinuten: number | null
  reisKm: number | null
  medewerker: string | null
}

export interface VerzamelbonWerkbonDTO {
  werkbonId: string
  werkbonNumber: string
  positie: number
  /** batch-67 (FO-507): null bij de urenvariant. */
  bedragExclBtw: string | null
  /** batch-67 (FO-507): de facturabele uren van deze bon (Decimal-string); null bij oude rijen zonder backfill. */
  totaalUren: string | null
  uitgevallenOp: string | null
  uitvalReden: VerzamelbonUitvalReden | null
  uitvalToelichting: string | null
}

export interface VerzamelbonDetailDTO {
  id: string
  nummer: string
  stamNummer: string
  revisie: number
  deelNr: number
  aantalDelen: number
  zendingId: string
  status: VerzamelbonStatus
  kanaal: VerzamelbonKanaal
  /** batch-67 (FO-507): UREN (geen bedragen) of MET_BEDRAGEN. */
  variant: VerzamelbonVariant
  /** batch-67 (FO-507): de som van de facturabele uren (Decimal-string). */
  totaalUren: string | null
  clientId: string
  clientName: string
  entityId: string
  entityName: string
  periodeVan: string
  periodeTot: string
  /** Null bij de urenvariant. */
  bedragExclBtw: string | null
  btwPercentage: string | null
  btwBedrag: string | null
  bedragInclBtw: string | null
  btwVerlegd: boolean
  /** Het facturatieverzoek (doorklik); null zolang er geen is of zonder FACTUREN.canView. */
  billingRequestId: string | null
  verzoekStatus: string | null
  verzondenOp: string | null
  verzondenDoorNaam: string | null
  vorige: { id: string; nummer: string } | null
  opvolger: { id: string; nummer: string; status: VerzamelbonStatus } | null
  vervangenOp: string | null
  vervangReden: string | null
  werkbonnen: VerzamelbonWerkbonDTO[]
  regels: VerzamelbonRegelDTO[]
  audits: Array<{ id: string; actie: string; op: string; doorNaam: string | null; toelichting: string | null }>
}

/**
 * batch-65 (FO-484 §6.2) — een bon uit een verzonden verzamelbon is later
 * gecrediteerd: het bundelverzoek blijft staan (de creditnota loopt via de
 * factuur), de koppeling van de bon in de verzamelbon wordt gemarkeerd.
 * POST /verzamelbonnen/:id/werkbonnen/:werkbonId/gecrediteerd.
 */
export const VerzamelbonBonGecrediteerdSchema = z.object({
  reden: z.string().trim().min(3, 'Geef een korte reden (minimaal 3 tekens).').max(500, 'Maximaal 500 tekens.'),
  creditnotaNummer: z.string().trim().max(60, 'Maximaal 60 tekens.').nullable().optional(),
})
export type VerzamelbonBonGecrediteerd = z.infer<typeof VerzamelbonBonGecrediteerdSchema>

export interface VerzamelbonLijstItemDTO {
  id: string
  nummer: string
  status: VerzamelbonStatus
  kanaal: VerzamelbonKanaal
  /** batch-67 (FO-507): UREN of MET_BEDRAGEN. */
  variant: VerzamelbonVariant
  clientName: string
  entityName: string
  periodeVan: string
  periodeTot: string
  aantalWerkbonnen: number
  /** Null bij de urenvariant. */
  bedragInclBtw: string | null
  /** batch-67 (FO-507): de som van de facturabele uren (Decimal-string). */
  totaalUren: string | null
  verzondenOp: string | null
  createdAt: string
}

export const VerzamelbonLijstQuerySchema = z.object({
  status: z.enum(VERZAMELBON_STATUSSEN).optional(),
  variant: z.enum(VERZAMELBON_VARIANTEN).optional(),
  clientId: z.string().min(1).optional(),
  page: z.coerce.number().int().min(1).default(1),
  pageSize: z.coerce.number().int().min(1).max(100).default(25),
})
export type VerzamelbonLijstQuery = z.infer<typeof VerzamelbonLijstQuerySchema>

// ─── Bundelvoorstel (spec §D.8) ──────────────────────────────────────────────

export interface FinqleBundelVoorstelDTO {
  sleutel: string
  clientId: string
  clientName: string
  entityId: string
  entityName: string
  soort: 'WEEK' | 'MAAND'
  periodeLabel: string
  periodeVan: string
  periodeTot: string
  /** De periode is voorbij: de bundel is compleet en kan nu weg. */
  afgesloten: boolean
  werkbonnen: Array<{ id: string; werkbonNumber: string }>
}

// ─── Losse verzending: verzamelbon aanbevolen (spec §D.8, T7) ────────────────

/**
 * Foutcode van de single-/bulk-Finqle-route wanneer een losse verzending een
 * verzamelbon had moeten zijn: meerdere bonnen van dezelfde klant x BV in één
 * losse bulk (n verzoeken bij Finqle), of een klant die per week/maand gebundeld
 * wil. Verzenden kan alsnog met `losBevestigd: true`; nooit stil.
 */
export const FINQLE_VERZAMELBON_AANBEVOLEN = 'FINQLE_VERZAMELBON_AANBEVOLEN' as const

export interface FinqleLosVerzendAdviesGroepDTO {
  clientId: string
  clientName: string
  entityId: string | null
  entityName: string | null
  aantal: number
  finqleBundelPeriode: FinqleBundelPeriode
}

export interface FinqleLosVerzendAdviesDTO {
  /** Per klant x BV in de selectie waar een verzamelbon passender is. */
  groepen: FinqleLosVerzendAdviesGroepDTO[]
  /** Kant-en-klare uitleg voor de bevestiging. */
  melding: string
  /**
   * Review batch-65 (H3) — bonnen die nog in een lopende verzamelbon zitten: los
   * versturen maakt bij Finqle een tweede verzoek naast dat van de bundel.
   */
  gebundeld?: FinqleLosVerzendGebundeldDTO[]
}

export interface FinqleLosVerzendGebundeldDTO {
  werkbonId: string
  werkbonNumber: string
  verzamelbonId: string
  verzamelbonNummer: string
}

// ─── Klantportaal (spec §C.6) - geen bedragen, geen interne velden ───────────

export interface KlantVerzamelbonWerkbonDTO {
  id: string
  werkbonNr: string
  startDatum: string
  eindDatum: string
  /** Uren zoals de klant ze accordeert (facturatie-as), zelfde afleiding als de lijst. */
  totalUren: number
  /** Staat deze bon nog open voor een klantbesluit (WACHT_OP_KLANT, geen besluit)? */
  openVoorBeoordeling: boolean
  /** Uit de bundel gevallen (bv. eerder door de klant afgekeurd). */
  uitgevallen: boolean
}

export interface KlantVerzamelbonDTO {
  id: string
  nummer: string
  aantalWerkbonnen: number
  periodeVan: string
  periodeTot: string
  /** ISO-week als de bundel binnen één week valt ("week 38"), anders null. */
  week: number | null
  /** Hoeveel bonnen nog op het klantbesluit wachten. */
  aantalOpen: number
  klantHandtekeningVereist: boolean
  werkbonnen: KlantVerzamelbonWerkbonDTO[]
}

/**
 * POST /klant/verzamelbonnen/:id/beoordeel. Eén akkoord op de hele bundel, of een
 * afkeuring met de keuze welke bon(nen) het betreft: die vallen uit de bundel en
 * de rest gaat door als nieuwe verzamelbon ("-2"). Alle bonnen afkeuren = één
 * actie (volledige afkeuring).
 */
export const KlantVerzamelbonBeoordelingSchema = z.discriminatedUnion('action', [
  z.object({
    action: z.literal('AKKOORD'),
    naam: z.string().trim().min(2, 'Vul uw naam in (minimaal 2 tekens).').max(120),
    /** base64 PNG data-URL; alleen als de relatie een handtekening eist. */
    handtekeningData: z.string().min(10).optional(),
  }),
  z.object({
    action: z.literal('AFKEUREN'),
    naam: z.string().trim().min(2, 'Vul uw naam in (minimaal 2 tekens).').max(120),
    reden: z.string().trim().min(3, 'Geef een korte reden (minimaal 3 tekens).').max(2000),
    werkbonIds: z.array(z.string().min(1)).min(1, 'Kies minstens één werkbon.').max(200),
  }),
])
export type KlantVerzamelbonBeoordeling = z.infer<typeof KlantVerzamelbonBeoordelingSchema>

export interface KlantVerzamelbonBeoordelingResultDTO {
  /** AKKOORD: de bundel is geaccordeerd; AFGEKEURD: alle bonnen; GESPLITST: de rest is een nieuwe verzamelbon. */
  uitkomst: 'AKKOORD' | 'AFGEKEURD' | 'GESPLITST'
  /** Bij GESPLITST: het nummer van de nieuwe verzamelbon ("VB-…-2"). */
  nieuwNummer: string | null
}
